import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  GET_EXPRESS2_GRAY_SWITCH,
  GetExpress2GraySwitchResponse,
} from '../graphql/queries/getExpress2GraySwitch';

/**
 * 揽收站点 2.0 灰度开关（mobile-web 司机端）。
 * 后端为单一源（pickup common_config `express2_gray_switch`，经 GraphQL getExpress2GraySwitch 读取），
 * 前后端读同一份名单。加载中/失败按未命中处理（fail-closed），保持与旧 localStorage 版本一致的对外 API。
 */
export interface Express2GraySwitch {
  wholeSwitch: boolean;
  hubIds: string[];
}

export function usePickupSiteV2Gray() {
  // 灰度是低频只读配置：cache-first 每次挂载至多拉一次，后续走缓存；失败/加载中回退全关
  const { data } = useQuery<GetExpress2GraySwitchResponse>(GET_EXPRESS2_GRAY_SWITCH, {
    fetchPolicy: 'cache-first',
  });

  const graySwitch = useMemo<Express2GraySwitch>(() => {
    const cfg = data?.getExpress2GraySwitch;
    return {
      wholeSwitch: !!cfg?.wholeSwitch,
      hubIds: Array.isArray(cfg?.hubIds) ? cfg!.hubIds.map(String) : [],
    };
  }, [data]);

  const express2Enabled = useCallback(
    (hubId?: string | number | null) => {
      // 失败关闭：无 hub 上下文时不启用 2.0（与 desktop 同口径），避免"任一 hub 灰度即对全部 hub 生效"
      if (hubId == null || hubId === '') return false;
      return graySwitch.wholeSwitch || graySwitch.hubIds.includes(String(hubId));
    },
    [graySwitch],
  );

  // 功能是否整体开启（用于菜单入口等无具体 hub 上下文的场景）
  const enabled = graySwitch.wholeSwitch || graySwitch.hubIds.length > 0;

  return { ...graySwitch, express2Enabled, enabled };
}
